import { useQuery } from '@apollo/client';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { PageNotFound } from '@openx/components-core/lib/PageNotFound/PageNotFound.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { AudienceStatus } from '@openx/types/targeting/audience.ts';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { GetAudienceDocument } from '../../graphql/query/audiences/GetAudience.generated.tsx';
import { AudienceFormFields } from './AudienceForm/AudienceFormFields.tsx';
import { useUpdateAndActivate } from './AudienceForm/hooks/useUpdateAndActivate.ts';
import type { AudienceForm } from './AudienceForm/types.ts';
import { getInitialEditValues } from './AudienceForm/utils/getInitialEditValues.ts';

export const EditAudiencePage = () => {
  const { t } = useTranslation();
  const { audienceUid = '' } = useParams<{
    audienceUid: string;
  }>();

  const {
    data: audienceData,
    error: audienceError,
    loading: audienceLoading,
  } = useQuery(GetAudienceDocument, { variables: { id: audienceUid } });

  const updateAndActivateAudience = useUpdateAndActivate(audienceUid);
  const initialData = audienceData?.audience_by_pk;

  if (audienceLoading) {
    return (
      <PageHeader title={t('Edit Audience')} titlePrefix="OpenXSelect">
        <Loader />
      </PageHeader>
    );
  }

  if (audienceError || !initialData) {
    enqueueSnackbar('Failed to load Audience', { variant: 'error' });

    return null;
  }

  const formValues = getInitialEditValues(initialData);
  const updateFn = async (values: AudienceForm) => {
    await updateAndActivateAudience(values, initialData);
  };

  const dataPending =
    initialData.status === AudienceStatus.EXPORTING || initialData.status === AudienceStatus.PUBLISHING;

  if (dataPending) {
    enqueueSnackbar('Cannot edit audience in pending state', { variant: 'error' });
    return <PageNotFound appName="OpenXSelect" />;
  }

  return (
    <PageHeader title={t('Edit Audience')} titlePrefix="OpenXSelect">
      <AudienceFormFields isEdit formValues={formValues} onSubmitHandler={updateFn} />
    </PageHeader>
  );
};
